import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { Grid, IconButton } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { ReactElement } from "react";
import { DatabaseAction } from "../../business-logic/ActionBehavior/Action";
import { EventGridRowModel } from "../../components/EventGrid";
import { useGetMatch } from "../../requests/matches";
import {
  DetailedEvent,
  PostMatchEventResponse,
  sendDeleteMatchEvents,
  sendPostMatchEvent,
  useUpdateDetailedEvent,
} from "../../requests/matchEvents";
import { useEventProcessingState } from "../useEventProcessingState";

export const extractDetailedEvent = (row: EventGridRowModel): DetailedEvent => {
  // Create a sample DetailedEvent object to extract the keys.
  const sampleDetailedEvent: DetailedEvent = {
    id: 0,
    match_id: 0,
    match_event_id: 0,
    action_type_id: DatabaseAction.kickoff,
    begin_team_id: 0,
    end_team_id: 0,
    event_time_in_ms: 0,
    loc_x: 0,
    loc_y: 0,
    is_deleted: false,
    is_comment: false,
    lead_time_in_ms: 0,
    tags: [],
  };

  const detailedEventKeys = Object.keys(sampleDetailedEvent);

  return Object.fromEntries(
    Object.entries(row).filter(([key]) => detailedEventKeys.includes(key))
  ) as DetailedEvent;
};

export const usePossessionTeamColumn = (): ((row: EventGridRowModel) => ReactElement) => {
  const getMatch = useGetMatch();
  const queryClient = useQueryClient();
  const { isEventProcessing, setEventProcessingState } = useEventProcessingState();

  const updatePossession = useUpdateDetailedEvent(
    async ({ detailedEvent }): Promise<PostMatchEventResponse> => {
      const postMatchEventResponse = sendPostMatchEvent(detailedEvent);
      const deleteMatchEventsResponse = sendDeleteMatchEvents(detailedEvent.match_id, [
        detailedEvent.match_event_id,
      ]);
      await deleteMatchEventsResponse;
      return await postMatchEventResponse;
    },
    (postMatchEventResponse) => {
      // Remove the event from processing state after successful completion
      setEventProcessingState(postMatchEventResponse.temp_match_event_id, false);

      queryClient.setQueriesData(["detailedEvent"], (prevDetailedEvents: DetailedEvent[] = []) =>
        prevDetailedEvents.map((prevDetailedEvent) =>
          prevDetailedEvent.match_id === postMatchEventResponse.match_id &&
          prevDetailedEvent.match_event_id === postMatchEventResponse.temp_match_event_id
            ? {
                ...prevDetailedEvent,
                ...postMatchEventResponse,
              }
            : prevDetailedEvent
        )
      );
    },
    (error) => {
      console.error("Failed to update possession:", error);
    }
  ).mutate;

  return (row) => {
    const match = getMatch(row.match_id);
    const getOtherTeam = (teamId: number) =>
      Number(teamId === match!.home_team_id ? match!.away_team_id : match!.home_team_id);

    const onClickSwap = () => {
      const newEvent: DetailedEvent = {
        ...extractDetailedEvent(row),
        begin_team_id: getOtherTeam(row.begin_team_id),
        end_team_id: getOtherTeam(row.end_team_id),
      };

      setEventProcessingState(newEvent.match_event_id, true);

      updatePossession({ detailedEvent: newEvent });
    };

    const isProcessing = isEventProcessing(row.match_event_id);

    return (
      <Grid container display="flex" direction="column">
        <div
          style={{
            textAlign: "center",
            marginTop: "0.2em",
            marginBottom: "-0.3em",
          }}
        >
          {row.possession_team.name}
        </div>
        <IconButton
          sx={{ mt: "-0.3em", mb: "-0.2em" }}
          onClick={onClickSwap}
          disabled={isProcessing}
        >
          <SwapHorizIcon fontSize="small" />
        </IconButton>
      </Grid>
    );
  };
};
